"use client";

import { useEffect, useRef, useState, useMemo } from "react";
import {
  createChart,
  type IChartApi,
  type ISeriesApi,
  type LineData,
  type Time,
} from "lightweight-charts";
import type { PerformanceSnapshot } from "@/stores/strategiesStore";
import { useStrategyPerformance } from "@/hooks/useStrategies";
import { TimeframeSelector, type Timeframe } from "./TimeframeSelector";

interface PerformanceChartProps {
  strategyId: string;
  strategyName: string;
  className?: string;
}

const CHART_COLORS = {
  background: "transparent",
  text: "#94a3b8",
  grid: "#1e293b",
  border: "#334155",
  crosshair: "#475569",
  line: "#3b82f6",
} as const;

export const PerformanceChart = ({
  strategyId,
  strategyName,
  className,
}: PerformanceChartProps) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const [timeframe, setTimeframe] = useState<Timeframe>("1Y");

  const { data, isLoading, error } = useStrategyPerformance(strategyId);

  const filteredData = useMemo(
    () => filterByTimeframe(data, timeframe),
    [data, timeframe]
  );

  const showChart = !isLoading && !error && filteredData.length > 0;

  // Initialize chart once
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 300,
      layout: {
        background: { color: CHART_COLORS.background },
        textColor: CHART_COLORS.text,
      },
      grid: {
        vertLines: { color: CHART_COLORS.grid },
        horzLines: { color: CHART_COLORS.grid },
      },
      rightPriceScale: { borderColor: CHART_COLORS.border },
      timeScale: {
        borderColor: CHART_COLORS.border,
        timeVisible: true,
        secondsVisible: false,
      },
      crosshair: {
        mode: 1,
        vertLine: {
          color: CHART_COLORS.crosshair,
          labelBackgroundColor: CHART_COLORS.border,
        },
        horzLine: {
          color: CHART_COLORS.crosshair,
          labelBackgroundColor: CHART_COLORS.border,
        },
      },
    });

    const series = chart.addLineSeries({
      color: CHART_COLORS.line,
      lineWidth: 2,
      crosshairMarkerVisible: true,
      crosshairMarkerRadius: 4,
      priceFormat: {
        type: "custom",
        formatter: (price: number) => `${price >= 0 ? "+" : ""}${price.toFixed(1)}%`,
      },
    });

    chartRef.current = chart;
    seriesRef.current = series;

    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, []);

  // Update data and resize chart when filtered data or visibility changes
  useEffect(() => {
    if (!chartRef.current || !seriesRef.current) return;

    // Sync width when chart transitions from hidden to visible
    if (showChart && chartContainerRef.current) {
      chartRef.current.applyOptions({
        width: chartContainerRef.current.clientWidth,
      });
    }

    const chartData: LineData[] = filteredData.map((s) => ({
      time: (new Date(s.timestamp).getTime() / 1000) as Time,
      value: s.cumulativeReturn,
    }));

    seriesRef.current.setData(chartData);
    chartRef.current.timeScale().fitContent();
  }, [filteredData, showChart]);

  return (
    <div className={className}>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-semibold text-white">{strategyName} Performance</h3>
        <TimeframeSelector value={timeframe} onChange={setTimeframe} />
      </div>

      <div className="relative">
        {/* Chart container — always mounted so the ref is available for initialization */}
        <div
          ref={chartContainerRef}
          className="rounded-lg border border-slate-200/60 bg-white/5"
          style={{ display: showChart ? undefined : "none" }}
        />

        {/* Overlay states */}
        {isLoading && <div className="h-[300px] animate-pulse rounded-lg bg-slate-800" />}

        {error && !isLoading && (
          <div className="flex h-[300px] items-center justify-center rounded-lg border border-rose-500/50 bg-rose-500/10">
            <p className="text-sm text-rose-400">Failed to load performance data</p>
          </div>
        )}

        {!isLoading && !error && !filteredData.length && (
          <div className="flex h-[300px] items-center justify-center rounded-lg border border-slate-200/60 bg-white/5">
            <p className="text-sm text-slate-400">No performance data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

function filterByTimeframe(
  data: PerformanceSnapshot[],
  timeframe: Timeframe
): PerformanceSnapshot[] {
  if (timeframe === "ALL" || !data.length) return data;

  const now = Date.now();
  const MS_PER_DAY = 86_400_000;
  const daysMap: Record<string, number> = {
    "7D": 7,
    "30D": 30,
    "90D": 90,
    "1Y": 365,
  };

  const cutoff = now - daysMap[timeframe] * MS_PER_DAY;
  return data.filter((s) => new Date(s.timestamp).getTime() >= cutoff);
}
